import React, { useCallback } from 'react';
import { Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, Star } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { StorefrontReviewData } from '../../../api';
import { ReviewCard } from './ReviewCard';

interface ReviewsSectionProps {
  reviews: StorefrontReviewData[];
}

export const ReviewsSection = ({ reviews }: ReviewsSectionProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = i18n.dir() === 'rtl';

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      direction: isRtl ? 'rtl' : 'ltr',
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })],
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  if (!reviews.length) {
    return null;
  }

  const averageScore = reviews.reduce((sum, r) => sum + r.score, 0) / reviews.length;

  return (
    <Grid container flexDirection="column" mb={isSmallScreen ? 6 : 10}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          marginBottom: 2,
          textAlign: 'center',
        }}>
        {t('storefront.whatCustomersSay')}
      </Typography>
      <Grid container alignItems="center" justifyContent="center" gap={1} mb={4}>
        <Grid container alignItems="center" width="auto">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              sx={{
                color: star <= Math.round(averageScore) ? '#FFD700' : '#E0E0E0',
                fontSize: 24,
              }}
            />
          ))}
        </Grid>
        <Typography variant="body1" color="text.secondary">
          ({reviews.length} {t('storefront.reviews')})
        </Typography>
      </Grid>
      <Grid container sx={{ position: 'relative' }}>
        {!isSmallScreen && reviews.length > 3 && (
          <IconButton
            onClick={scrollPrev}
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' },
            }}>
            {isRtl ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
        <Grid ref={emblaRef} sx={{ overflow: 'hidden', padding: '16px 4px', margin: '-16px -4px', width: '100%' }}>
          <Grid container flexWrap="nowrap" gap={3}>
            {reviews.map((review, index) => (
              <Grid
                key={review.id || index}
                sx={{
                  flex: isSmallScreen ? '0 0 100%' : '0 0 calc(33.333% - 16px)',
                  minWidth: 0,
                }}>
                <ReviewCard review={review} />
              </Grid>
            ))}
          </Grid>
        </Grid>
        {!isSmallScreen && reviews.length > 3 && (
          <IconButton
            onClick={scrollNext}
            sx={{
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' },
            }}>
            {isRtl ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};
